import { useEffect, useState } from "react";

/**
 * A message the host site sent on the guest's behalf, typed out instead of
 * appearing whole. The point is authorship: the guest clicked "discover more"
 * on a room card and a question they never wrote shows up as theirs, so the
 * few hundred milliseconds of typing are what make it read as their turn
 * rather than as something the page did behind their back.
 *
 * Capped in total duration, so a long injected question is quick per character
 * rather than slow overall. Respects prefers-reduced-motion by skipping to the
 * full text.
 */

const TOTAL_DURATION_MS = 520;
const MIN_STEP_MS = 12;

export default function InjectedText({ text }: { text: string }) {
  const [revealed, setRevealed] = useState(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return reduced ? text.length : 0;
  });

  useEffect(() => {
    if (revealed >= text.length) return;

    const step = Math.max(MIN_STEP_MS, TOTAL_DURATION_MS / text.length);
    const timer = window.setInterval(() => {
      setRevealed((previous) => {
        if (previous >= text.length) {
          window.clearInterval(timer);
          return previous;
        }
        return previous + 1;
      });
    }, step);

    return () => window.clearInterval(timer);
    // Runs once per message: `text` never changes for a given bubble, and
    // re-running on `revealed` would restart the interval on every character.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const done = revealed >= text.length;

  return (
    <p className="injected-message-text">
      {text.slice(0, revealed)}
      {!done && <span className="injected-message-caret" aria-hidden="true" />}
    </p>
  );
}
